import { Box, IconButton, styled } from "@mui/material"
import React, { useState, useRef, useEffect } from "react"
import SortingToggleIcon from "assets/icons/SortingToggleIcon.svg"
import AppTextField from "components/StyledComponents/AppTextField"
import GreenCheckedIcon from "assets/icons/GreenCheckedIcon.svg"
import ClearIcon from "@mui/icons-material/Clear"
import { Col, Row } from "react-bootstrap"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import { marketingActions } from "redux/marketing/slice/user"
import { useDispatch } from "react-redux"
import ImageCropper from "components/ImageCropper"
import { uploadTastingNotesImagesAction } from "redux/marketing/actions"
import styles from "./optionTemplate.module.scss"

const StyledTextField = styled(AppTextField)(({ theme }) => ({
  "& .MuiInputBase-input": {
    border: "none !important",
    borderTopLeftRadius: "0px !important",
    borderBottomLeftRadius: "0px !important"
  },
  "& .MuiFilledInput-root": {
    border: `1px solid ${theme.palette.text.secondary} !important`,
    borderRadius: 6
  },
  "& .Mui-error": {
    borderColor: `${theme.palette.error.main} !important`,
    "& .MuiInputBase-input": {
      border: "none"
    }
  },
  "& .startAdornament": {
    width: 20,
    height: 20,
    backgroundColor: theme.palette.background.default,
    display: "flex",
    paddingLeft: 10,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 2
  }
}))

const OptionTemplate = ({
  formik,
  index,
  dragStart,
  dragEnter,
  drop,
  arrayHelper,
  getOptionValues = () => {},
  getOptionPath = () => {},
  questionType
}) => {
  const dispatch = useDispatch()
  const [optionText, setOptionText] = useState("")
  const [openCrop, setCrop] = useState(false)
  const [fileDetails, setFileDetails] = useState(null)
  const [imageObjectURL, setImageObjectUrl] = useState(null)
  const [indexValue, setIndex] = useState(0)
  const timeoutRef = useRef(null)
  const optionErrors = getOptionValues(formik?.errors)?.[index] || {}
  const optionTouched = getOptionValues(formik?.touched)?.[index] || {}

  const onCorrectOptionClick = (index) => {
    const newOptions = [...(getOptionValues(formik.values) || [])]

    if (questionType === "4-images") {
      newOptions[index].isCorrect = !newOptions[index].isCorrect
      formik.setFieldValue(`${getOptionPath()}`, newOptions)
    } else {
      if (newOptions[index]?.isCorrect) {
        newOptions[index].isCorrect = false
      } else {
        newOptions.forEach((option) => {
          option.isCorrect = false
        })
        newOptions[index].isCorrect = true
      }
      formik.setFieldValue(`${getOptionPath()}`, newOptions)
    }
  }

  const onOptionTextChange = (e) => {
    const value = e.target.value
    setOptionText(value)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(
      () => formik.setFieldValue(`${getOptionPath()}.[${index}].name`, value),
      800
    )
  }

  const onOptionTextBlur = (e) => {
    const value = e.target.value
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    formik.setFieldValue(`${getOptionPath()}.[${index}].name`, value)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const onImageUploadItem = (image, index) => {
    if (image) {
      setCrop(true)
      setFileDetails(image)
      setIndex(index)
      setImageObjectUrl(URL.createObjectURL(image))
    }
  }

  const handleImageDeleteItem = (formik, index) => {
    dispatch(marketingActions.setTastingNotesImages({ index, id: null }))
    formik.setFieldValue(`${getOptionPath()}.[${index}].image`, "")
  }

  function handleCroppedImage(image) {
    let data = new FormData()
    data.append("media", image)
    data.append("media_type", "IMAGE")
    data.append("content_type", image.type)
    dispatch(uploadTastingNotesImagesAction({ indexValue, data }))
    formik.setFieldValue(`${getOptionPath()}.[${indexValue}].image`, image)
  }

  function showCropperModal(imageObjectURL, fileDetails) {
    return (
      <ImageCropper
        setSelectedImage={(image) => handleCroppedImage(image)}
        srcImage={imageObjectURL}
        fileDetail={fileDetails}
        aspectRatio={0}
        onClose={setCrop}
        openCrop={openCrop}></ImageCropper>
    )
  }

  useState(() => {
    setOptionText(getOptionValues(formik.values)?.[index]?.name)
  }, [])

  return (
    <>
      {showCropperModal(imageObjectURL, fileDetails)}
      <li
        key={`optionContainer-${index}`}
        style={{ listStyleType: "none", marginBottom: 20 }}
        className="optionItem"
        data-id={index}>
        <Box sx={{ maxWidth: "850px" }} className="d-flex">
          <Box
            className="d-flex justify-content-center align-items-start"
            sx={{ cursor: "grab" }}
            draggable
            onDragStart={(e) => dragStart(e, index)}
            onDragEnter={dragEnter}
            onDragEnd={() => drop(arrayHelper)}>
            <img src={SortingToggleIcon} alt="sort option" className="flex-shrink-0 me-1 mt-3" />
          </Box>
          <Row className="w-100 m-0">
            <Col lg="6">
              <StyledTextField
                fullWidth
                placeholder="Enter Option"
                className="flex-shrink-1 flex-grow-1 me-5"
                error={optionTouched.name && Boolean(optionErrors.name)}
                helperText={optionTouched.name && optionErrors.name}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <Box className="startAdornament">{(index + 10).toString(36).toUpperCase()}</Box>
                  )
                }}
                value={optionText}
                onChange={onOptionTextChange}
                onBlur={onOptionTextBlur}
              />
            </Col>
            <Col lg="4">
              <ImageUploadField
                isVariant={true}
                placeholder="Upload Image"
                value={getOptionValues(formik.values)?.[index]?.image}
                onChange={(e) => onImageUploadItem(e, index)}
                onDelete={() => handleImageDeleteItem(formik, index)}
                error={optionErrors.image}
                touched={optionTouched.image}
                helperText={optionTouched.image && optionErrors.image}
              />
            </Col>
            <Col lg="1">
              <span onClick={() => onCorrectOptionClick(index)} className="flex-shrink-0">
                <IconButton sx={{ height: 48, width: 48, padding: 0 }}>
                  {getOptionValues(formik.values)?.[index]?.isCorrect ? (
                    <img src={GreenCheckedIcon} alt="Checked" />
                  ) : (
                    <Box className={styles.checkbox} />
                  )}
                </IconButton>
              </span>
            </Col>
            <Col lg="1">
              {getOptionValues(formik.values).length > 1 ? (
                <span role="button" onClick={() => arrayHelper.remove(index)}>
                  <ClearIcon sx={{ width: "30px", height: "48px" }} />
                </span>
              ) : null}
            </Col>
          </Row>
        </Box>
      </li>
    </>
  )
}

export default OptionTemplate
